import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import './VisitorHistory.css';

const VisitorHistory = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 20,
        offset: 0,
        currentPage: 1,
        totalPages: 0,
    });
    const [filters, setFilters] = useState({
        start_date: searchParams.get('start_date') || '',
        end_date: searchParams.get('end_date') || '',
    });

    // 방문 기록 조회
    useEffect(() => {
        if (!user) {
            console.log('사용자 정보 로딩 중...');
            setLoading(false);
            return;
        }
        
        console.log('User info:', user);
        console.log('방문 기록 조회 시작');
        fetchVisits();
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pagination.offset, filters]);

    const fetchVisits = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('방문 기록 조회 시작');

            const params = {
                limit: pagination.limit,
                offset: pagination.offset,
                // cemetery_id는 백엔드에서 JWT 토큰으로부터 자동 추출
            };

            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;

            const response = await api.get('/api/admin/visit-history', { params });
            
            console.log('API 응답:', response.data);

            if (response.data.success) {
                setVisits(response.data.data.visits || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.data.total,
                    totalPages: Math.ceil(response.data.data.total / prev.limit),
                }));
                console.log('방문 기록 조회 완료:', response.data.data.visits.length, '건');
            } else {
                console.error('API 응답 실패:', response.data);
                setError('방문 기록을 불러오는데 실패했습니다.');
                setVisits([]);
            }
        } catch (error) {
            console.error('방문 기록 조회 오류:', error);
            setError('방문 기록을 불러오는데 실패했습니다.');
            setVisits([]); // 에러 시 빈 배열로 설정
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, offset: 0, currentPage: 1 }));
        
        // URL 파라미터 업데이트
        const newParams = { ...Object.fromEntries(searchParams) };
        if (value) {
            newParams[key] = value;
        } else {
            delete newParams[key];
        }
        setSearchParams(newParams);
    };

    const handlePageChange = (newPage) => {
        const newOffset = (newPage - 1) * pagination.limit;
        setPagination(prev => ({
            ...prev,
            offset: newOffset,
            currentPage: newPage,
        }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleResetFilters = () => {
        setFilters({
            start_date: '',
            end_date: '',
        });
        setPagination(prev => ({ ...prev, offset: 0, currentPage: 1 }));
        setSearchParams({});
    };

    const formatCoordinate = (lat, lng) => {
        if (!lat || !lng) return '-';
        return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
    };

    if (loading && pagination.currentPage === 1) {
        return (
            <div className="visitor-history-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>방문 기록을 불러오는 중...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="visitor-history-container">
            <div className="page-header">
                <h1>방문자 목록</h1>
                <p className="page-description">앱 사용자의 묘지 방문 기록을 확인할 수 있습니다.</p>
            </div>

            {/* 필터 */}
            <div className="filter-section">
                <h2>필터</h2>
                <div className="filter-grid">
                    <div className="filter-item">
                        <label>시작 날짜</label>
                        <input 
                            type="date" 
                            value={filters.start_date}
                            onChange={(e) => handleFilterChange('start_date', e.target.value)}
                        />
                    </div>
                    <div className="filter-item">
                        <label>종료 날짜</label>
                        <input 
                            type="date" 
                            value={filters.end_date}
                            onChange={(e) => handleFilterChange('end_date', e.target.value)}
                        />
                    </div>
                    <div className="filter-item filter-actions">
                        <button className="btn-reset" onClick={handleResetFilters}>
                            초기화
                        </button>
                    </div>
                </div>
            </div>

            {/* 방문 기록 테이블 */}
            {error ? (
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchVisits} className="btn-retry">다시 시도</button>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="visits-table">
                            <thead>
                                <tr>
                                    <th>방문자</th>
                                    <th>연락처</th>
                                    <th>묘적번호</th>
                                    <th>목적지 (사망자명)</th>
                                    <th>위치 (위도, 경도)</th>
                                    <th>방문 일시</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!Array.isArray(visits) ? (
                                    <tr>
                                        <td colSpan="6" className="no-data">
                                            데이터를 불러오는 중...
                                        </td>
                                    </tr>
                                ) : visits.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="no-data">
                                            방문 기록이 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    visits.map(visit => (
                                        <tr key={visit.id}>
                                            <td>
                                                <div className="user-cell">
                                                    <span className="user-name">
                                                        {visit.user?.name || '-'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="phone-number">
                                                    {visit.user?.phone || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="member-number">
                                                    {visit.member_number || visit.deceased?.member_number || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="deceased-name">
                                                    {visit.deceased?.name || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="location-cell">
                                                    <div className="location-item">
                                                        <span className="location-label">사용자:</span>
                                                        <span className="location-value">
                                                            {formatCoordinate(
                                                                visit.user_location?.latitude,
                                                                visit.user_location?.longitude
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="location-item">
                                                        <span className="location-label">목적지:</span>
                                                        <span className="location-value">
                                                            {formatCoordinate(
                                                                visit.destination_location?.latitude,
                                                                visit.destination_location?.longitude
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="visit-date">
                                                    {formatDateTime(visit.visited_at)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    {pagination.totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.currentPage === 1}
                            >
                                처음
                            </button>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                이전
                            </button>
                            
                            <div className="pagination-info">
                                <span>{pagination.currentPage}</span>
                                <span className="separator">/</span>
                                <span>{pagination.totalPages}</span>
                            </div>

                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === pagination.totalPages}
                            >
                                다음
                            </button>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={pagination.currentPage === pagination.totalPages}
                            >
                                마지막
                            </button>
                        </div>
                    )}

                    {/* 전체 레코드 정보 */}
                    <div className="records-info">
                        총 {pagination.total?.toLocaleString() || 0}건의 방문 기록
                    </div>
                </>
            )}
        </div>
    );
};

export default VisitorHistory;

